import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router';
import { HeaderBar } from '../components/HeaderBar.tsx';
import { useMe } from '../api/hooks.ts';
import { useSleep, useFeed, usePump, useDiaper } from '../api/events.ts';
import {
  computeDailySleepTotals,
  computeDayStats,
  computeHeatmap,
  computeWeekBuckets,
} from '../lib/stats.ts';
import { fmtDur } from '../lib/format.ts';
import { DayTimelineSheet } from '../components/sheets/DayTimelineSheet.tsx';

type Tab = 'day' | 'week' | 'month';

export function Stats() {
  const { tab } = useParams<{ tab: Tab }>();
  const t: Tab = tab === 'week' || tab === 'month' ? tab : 'day';
  const { data: me } = useMe();
  const baby = me?.babies.find((b) => b.id === me?.active_baby_id) ?? me?.babies[0] ?? null;

  const w = useMemo(() => {
    const nowMs = Date.now();
    const monthFrom = Math.floor(new Date(nowMs - 29 * 86400_000).setHours(0, 0, 0, 0) / 1000);
    const to = Math.floor(nowMs / 60_000) * 60 + 3600;
    return { from: monthFrom, to, babyId: baby?.id };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baby?.id, Math.floor(Date.now() / 60_000)]);
  const sleep = useSleep(w).data ?? [];
  const feed = useFeed(w).data ?? [];
  const pump = usePump(w).data ?? [];
  const diaper = useDiaper(w).data ?? [];

  return (
    <>
      <HeaderBar
        baby={baby}
        title="Statistics"
        subtitle={t === 'week' ? 'Last 7 days' : t === 'month' ? 'Last 28 days' : undefined}
      />
      <Tabs active={t} />
      <div className="lt-scroll" style={{ flex: 1 }}>
        {t === 'day' && <DayStats sleep={sleep} feed={feed} pump={pump} diaper={diaper} />}
        {t === 'week' && (
          <WeekStats
            sleep={sleep}
            feed={feed}
            pump={pump}
            diaper={diaper}
            weekStart={me?.settings?.week_start ?? 'monday'}
          />
        )}
        {t === 'month' && <MonthStats sleep={sleep} pump={pump} feed={feed} />}
      </div>
    </>
  );
}

function Tabs({ active }: { active: Tab }) {
  const items: { id: Tab; l: string }[] = [
    { id: 'day', l: 'Day' },
    { id: 'week', l: 'Week' },
    { id: 'month', l: 'Month' },
  ];
  return (
    <div style={{ padding: '0 16px 14px' }}>
      <div
        style={{
          display: 'flex',
          padding: 4,
          background: 'var(--bg-sunken)',
          borderRadius: 999,
          gap: 4,
        }}
      >
        {items.map((it) => {
          const on = it.id === active;
          return (
            <Link
              key={it.id}
              to={`/stats/${it.id}`}
              style={{
                flex: 1,
                textAlign: 'center',
                padding: '8px 0',
                borderRadius: 999,
                fontSize: 13,
                fontWeight: 600,
                background: on ? 'var(--bg-elevated)' : 'transparent',
                color: on ? 'var(--text-1)' : 'var(--text-3)',
                boxShadow: on ? 'var(--shadow-sm)' : 'none',
                letterSpacing: -0.005,
                textDecoration: 'none',
              }}
            >
              {it.l}
            </Link>
          );
        })}
      </div>
    </div>
  );
}

function BigStat({
  label,
  value,
  sub,
  accent,
}: {
  label: React.ReactNode;
  value: string;
  sub?: React.ReactNode;
  accent?: string;
}) {
  return (
    <div
      style={{
        padding: 16,
        borderRadius: 18,
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
      }}
    >
      <div
        style={{
          fontSize: 11,
          fontWeight: 600,
          color: 'var(--text-3)',
          textTransform: 'uppercase',
          letterSpacing: 0.04,
        }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginTop: 6 }}>
        <span
          className="lt-serif lt-tabular"
          style={{
            fontSize: 30,
            lineHeight: 1,
            fontWeight: 400,
            letterSpacing: -0.015,
            color: accent ? `var(--${accent})` : 'var(--text-1)',
          }}
        >
          {value}
        </span>
      </div>
      {sub && <div style={{ marginTop: 6 }}>{sub}</div>}
    </div>
  );
}

function FeedTag({ c, l }: { c: string; l: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 12, color: 'var(--text-2)' }}>
      <span style={{ width: 6, height: 6, borderRadius: 999, background: c }} />
      {l}
    </span>
  );
}

function startOfDay(d: Date): Date {
  const c = new Date(d);
  c.setHours(0, 0, 0, 0);
  return c;
}

function DateNav({
  label,
  canForward,
  onPrev,
  onNext,
  onToday,
  showToday,
}: {
  label: string;
  canForward: boolean;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  showToday: boolean;
}) {
  const btn = {
    width: 36,
    height: 36,
    borderRadius: 999,
    background: 'var(--bg-card)',
    border: '1px solid var(--border)',
    color: 'var(--text-2)',
    fontSize: 18,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  } as const;
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 8,
        marginBottom: 4,
      }}
    >
      <button type="button" onClick={onPrev} style={btn} aria-label="Previous day">‹</button>
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-1)' }}>{label}</span>
        {showToday && (
          <button
            type="button"
            onClick={onToday}
            style={{
              padding: '4px 10px',
              borderRadius: 999,
              background: 'var(--bg-card)',
              border: '1px solid var(--border)',
              color: 'var(--text-2)',
              fontSize: 11,
              fontWeight: 600,
              letterSpacing: 0.04,
              textTransform: 'uppercase',
              cursor: 'pointer',
            }}
          >
            Today
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={onNext}
        disabled={!canForward}
        style={{ ...btn, opacity: canForward ? 1 : 0.35, cursor: canForward ? 'pointer' : 'default' }}
        aria-label="Next day"
      >
        ›
      </button>
    </div>
  );
}

function DayStats({ sleep, feed, pump, diaper }: any) {
  const [date, setDate] = useState<Date>(() => startOfDay(new Date()));
  const today = startOfDay(new Date()).getTime();
  const isToday = date.getTime() === today;
  const isFuture = date.getTime() >= today;

  const s = useMemo(
    () => computeDayStats({ sleep, feed, pump, diaper, date }),
    [sleep, feed, pump, diaper, date],
  );
  // Only pulse the tonight card when we're actually in a live night session
  // AND viewing today — a closed bedtime that ended is informative but
  // shouldn't blink.
  const tonightActive = s.tonightSoFarMin > 0;
  const tonightLive = isToday && tonightActive && sleep.some((x: any) => x.ended_at == null);

  const shift = (days: number) => {
    const next = new Date(date);
    next.setDate(next.getDate() + days);
    if (next.getTime() > today) return;
    setDate(startOfDay(next));
  };

  const label =
    date.getTime() === today
      ? 'Today'
      : date.getTime() === today - 86400_000
        ? 'Yesterday'
        : date.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' });

  return (
    <div style={{ padding: '0 16px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <DateNav label={label} canForward={!isFuture} onPrev={() => shift(-1)} onNext={() => shift(1)} onToday={() => setDate(startOfDay(new Date()))} showToday={!isToday} />
      {tonightActive && (
        <BigStat
          label={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              Tonight so far
              <span
                aria-hidden
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: 'var(--accent)',
                  animation: tonightLive ? 'lt-pulse 1.4s ease-in-out infinite' : 'none',
                  opacity: tonightLive ? 1 : 0.45,
                }}
              />
            </span>
          }
          value={fmtDur(s.tonightSoFarMin)}
          accent="sleep-night"
          sub={
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
              {tonightLive ? 'in-progress night sleep' : 'evening bedtime'}
            </div>
          }
        />
      )}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <BigStat
          label="Last night"
          value={s.lastNightMin > 0 ? fmtDur(s.lastNightMin) : '—'}
          accent="sleep-night"
        />
        <BigStat
          label="Naps today"
          value={s.napsMin > 0 ? fmtDur(s.napsMin) : '—'}
          accent="sleep-day"
          sub={
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
              across {s.naps} session{s.naps === 1 ? '' : 's'}
            </div>
          }
        />
      </div>
      <BigStat
        label="Total sleep"
        value={fmtDur(s.sleepTotalMin)}
        sub={
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            {tonightActive ? 'tonight + last night + naps today' : 'last night + naps today'}
          </div>
        }
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <BigStat label="Longest stretch" value={fmtDur(s.longestStretchMin)} />
        <BigStat label="Naps" value={String(s.naps)} />
      </div>

      <div style={{ padding: 16, borderRadius: 18, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.04 }}>
          Wake windows
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 12, alignItems: 'baseline' }}>
          {s.wakeWindows.length === 0 && <div style={{ color: 'var(--text-3)', fontSize: 13 }}>—</div>}
          {s.wakeWindows.map((w, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center' }}>
              <div className="lt-serif lt-tabular" style={{ fontSize: 18, fontWeight: 400 }}>
                {fmtDur(w)}
              </div>
              <div className="lt-mono" style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 2 }}>
                #{i + 1}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <BigStat
          label="Feeds"
          value={String(s.feedCount)}
          sub={
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
              <FeedTag c="var(--bottle)" l={`${s.feedsByType.bottle} bot`} />
              <FeedTag c="var(--breast)" l={`${s.feedsByType.breast} br`} />
              <FeedTag c="var(--solid)" l={`${s.feedsByType.solid} sol`} />
            </div>
          }
        />
        <BigStat
          label="Diapers"
          value={String(s.diaperCount)}
          sub={
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 8px' }}>
              <FeedTag c="var(--pee)" l={`${s.diapersByKind.pee} pee`} />
              <FeedTag c="var(--poop)" l={`${s.diapersByKind.poop} poop`} />
              <FeedTag c="var(--poop)" l={`${s.diapersByKind.both} both`} />
            </div>
          }
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <BigStat label="Bottle volume" value={`${s.bottleMl} ml`} accent="bottle" />
        <BigStat
          label="Pump yield"
          value={`${s.pumpYieldMl} ml`}
          accent="pump"
          sub={
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
              across {s.pumpCount} session{s.pumpCount === 1 ? '' : 's'}
            </div>
          }
        />
      </div>
    </div>
  );
}

function WeekStats({
  sleep,
  feed,
  pump,
  diaper,
  weekStart,
}: {
  sleep: any;
  feed: any;
  pump: any;
  diaper: any;
  weekStart: 'monday' | 'sunday';
}) {
  const [tappedDate, setTappedDate] = useState<Date | null>(null);
  const buckets = useMemo(
    () => computeWeekBuckets({ sleep, feed, pump, diaper, end: new Date(), weekStart }),
    [sleep, feed, pump, diaper, weekStart],
  );
  const max = Math.max(1, ...buckets.map((b) => b.sleepNightMin + b.sleepDayMin));
  const totalSleep = buckets.reduce((a, b) => a + b.sleepNightMin + b.sleepDayMin, 0);
  const avgSleep = Math.round(totalSleep / 7);
  const best = buckets.reduce(
    (acc, b) => (b.sleepNightMin + b.sleepDayMin > acc.t ? { d: b.date, t: b.sleepNightMin + b.sleepDayMin } : acc),
    { d: buckets[0]?.date, t: 0 },
  );
  const worst = buckets.reduce(
    (acc, b) => (b.sleepNightMin + b.sleepDayMin < acc.t ? { d: b.date, t: b.sleepNightMin + b.sleepDayMin } : acc),
    { d: buckets[0]?.date, t: Infinity },
  );

  const dow = (d?: Date) => (d ? ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()] : '—');

  return (
    <div style={{ padding: '0 16px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <BigStat label="Avg sleep / day" value={fmtDur(avgSleep)} />
        <BigStat
          label="Total sleep"
          value={fmtDur(totalSleep)}
          sub={<div style={{ fontSize: 12, color: 'var(--text-3)' }}>across the 7 days</div>}
        />
      </div>

      <div style={{ padding: 16, borderRadius: 18, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
          }}
        >
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.04 }}>
            Sleep by day
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-3)' }}>night / day</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'stretch', gap: 10, marginTop: 16, height: 130 }}>
          {buckets.map((b, i) => {
            const totalH = ((b.sleepNightMin + b.sleepDayMin) / max) * 100;
            const sum = Math.max(1, b.sleepNightMin + b.sleepDayMin);
            const nightH = (b.sleepNightMin / sum) * totalH;
            const dayH = (b.sleepDayMin / sum) * totalH;
            return (
              <button
                type="button"
                key={i}
                onClick={() => setTappedDate(b.date)}
                style={{ all: 'unset', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, cursor: 'pointer' }}
              >
                <div style={{ width: '100%', flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                  <div style={{ width: '100%', height: `${dayH}%`, background: 'var(--sleep-day)', borderRadius: '4px 4px 0 0' }} />
                  <div style={{ width: '100%', height: `${nightH}%`, background: 'var(--sleep-night)' }} />
                </div>
                <div style={{ fontSize: 10, fontWeight: 500, color: 'var(--text-3)' }}>{dow(b.date)}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        <div style={{ padding: 14, borderRadius: 16, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--good)', textTransform: 'uppercase' }}>Best</div>
          <div className="lt-serif lt-tabular" style={{ fontSize: 22, marginTop: 4 }}>{fmtDur(best.t)}</div>
          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{dow(best.d)}</div>
        </div>
        <div style={{ padding: 14, borderRadius: 16, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--danger)', textTransform: 'uppercase' }}>Worst</div>
          <div className="lt-serif lt-tabular" style={{ fontSize: 22, marginTop: 4 }}>
            {worst.t === Infinity ? '—' : fmtDur(worst.t)}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{dow(worst.d)}</div>
        </div>
      </div>

      <SmallChart
        title="Feeds / day"
        values={buckets.map((b) => b.feedCount)}
        color="var(--bottle)"
        labels={buckets.map((b) => dow(b.date))}
        onTap={(i) => setTappedDate(buckets[i]!.date)}
      />
      <SmallChart
        title="Pump yield / day (ml)"
        values={buckets.map((b) => b.pumpYieldMl)}
        color="var(--pump)"
        labels={buckets.map((b) => dow(b.date))}
        onTap={(i) => setTappedDate(buckets[i]!.date)}
      />
      <SmallChart
        title="Diapers / day"
        values={buckets.map((b) => b.diaperCount)}
        color="var(--poop)"
        labels={buckets.map((b) => dow(b.date))}
        onTap={(i) => setTappedDate(buckets[i]!.date)}
      />

      <DayTimelineSheet
        open={!!tappedDate}
        date={tappedDate}
        sleep={sleep}
        feed={feed}
        pump={pump}
        diaper={diaper}
        onClose={() => setTappedDate(null)}
      />
    </div>
  );
}

function SmallChart({
  title,
  values,
  color,
  labels,
  onTap,
}: {
  title: string;
  values: number[];
  color: string;
  labels: string[];
  onTap?: (index: number) => void;
}) {
  const max = Math.max(1, ...values);
  return (
    <div style={{ padding: 14, borderRadius: 16, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: 0.04 }}>{title}</div>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: 4, marginTop: 12, height: 70 }}>
        {values.map((v, i) => (
          <button
            type="button"
            key={i}
            onClick={() => onTap?.(i)}
            style={{ all: 'unset', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, cursor: onTap ? 'pointer' : 'default' }}
          >
            <div style={{ width: '100%', flex: 1, display: 'flex', alignItems: 'flex-end' }}>
              <div style={{ width: '100%', height: `${(v / max) * 100}%`, background: color, borderRadius: '3px 3px 0 0', minHeight: 2 }} />
            </div>
            <div style={{ fontSize: 9, color: 'var(--text-3)' }}>{labels[i]}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

function MonthStats({ sleep, pump, feed }: any) {
  const heat = useMemo(() => computeHeatmap({ sleep, days: 28, endDate: new Date() }), [sleep]);
  const trend = useMemo(
    () => computeDailySleepTotals({ sleep, days: 28, endDate: new Date() }),
    [sleep],
  );

  return (
    <div style={{ padding: '0 16px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ padding: 16, borderRadius: 18, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase' }}>Sleep trend · 28d</div>
        <SleepTrend values={trend} />
      </div>

      <div style={{ padding: 16, borderRadius: 18, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase' }}>Hour-of-day · asleep</div>
          <div style={{ fontSize: 10, color: 'var(--text-3)' }}>rows = days</div>
        </div>
        <Heatmap data={heat.rows} />
      </div>

      <div style={{ padding: 16, borderRadius: 18, background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase' }}>Feed intervals · 28d</div>
        <FeedIntervalHistogram feed={feed} />
      </div>
    </div>
  );
}

function SleepTrend({ values }: { values: number[] }) {
  const w = 320, h = 100, pad = 8;
  const max = Math.max(1, ...values);
  const min = Math.min(0, ...values);
  const pts = values.map((v, i) => {
    const x = pad + (i / Math.max(1, values.length - 1)) * (w - pad * 2);
    const y = pad + (1 - (v - min) / (max - min || 1)) * (h - pad * 2);
    return [x, y] as [number, number];
  });
  const line = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ');
  const area = `${line} L${pts[pts.length - 1]?.[0] ?? 0},${h - pad} L${pts[0]?.[0] ?? 0},${h - pad} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ width: '100%', height: 110, marginTop: 8 }}>
      <defs>
        <linearGradient id="trendGrad" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="var(--accent)" stopOpacity="0.25" />
          <stop offset="100%" stopColor="var(--accent)" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#trendGrad)" />
      <path d={line} fill="none" stroke="var(--accent)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => (
        <circle key={i} cx={p[0]} cy={p[1]} r={i === pts.length - 1 ? 3 : 1.2} fill="var(--accent)" />
      ))}
    </svg>
  );
}

function Heatmap({ data }: { data: number[][] }) {
  const cols = 24;
  return (
    <div style={{ marginTop: 10 }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          fontFamily: 'Geist Mono, ui-monospace',
          fontSize: 9,
          color: 'var(--text-3)',
          marginBottom: 4,
          padding: '0 2px',
        }}
      >
        {[0, 6, 12, 18, 24].map((h) => (
          <span key={h}>{String(h).padStart(2, '0')}</span>
        ))}
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${cols}, 1fr)`,
          gridAutoRows: '6px',
          gap: 1,
        }}
      >
        {data.flatMap((row, ri) =>
          row.map((v, ci) => (
            <div
              key={`${ri}-${ci}`}
              style={{
                background:
                  v < 0.1
                    ? 'var(--bg-sunken)'
                    : `color-mix(in oklab, var(--sleep-night) ${Math.round(v * 100)}%, var(--bg-sunken))`,
                borderRadius: 1,
              }}
            />
          )),
        )}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 8, fontSize: 10, color: 'var(--text-3)' }}>
        <span>awake</span>
        <div
          style={{
            flex: 1,
            height: 4,
            borderRadius: 2,
            background: 'linear-gradient(to right, var(--bg-sunken), var(--sleep-night))',
          }}
        />
        <span>asleep</span>
      </div>
    </div>
  );
}

function FeedIntervalHistogram({ feed }: { feed: any[] }) {
  // Compute intervals between consecutive feeds across the window (minutes)
  const sorted = [...feed].sort((a, b) => a.started_at - b.started_at);
  const intervals: number[] = [];
  for (let i = 1; i < sorted.length; i++) {
    intervals.push(Math.floor((sorted[i].started_at - sorted[i - 1].started_at) / 60));
  }
  const bins = [60, 90, 120, 150, 180, 210, 240];
  const counts = bins.map((b, i) => {
    const next = bins[i + 1] ?? Infinity;
    return intervals.filter((m) => m >= b && m < next).length;
  });
  const max = Math.max(1, ...counts);
  return (
    <div style={{ display: 'flex', alignItems: 'stretch', gap: 6, marginTop: 12, height: 80 }}>
      {bins.map((b, i) => (
        <div key={b} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
          <div style={{ width: '100%', flex: 1, display: 'flex', alignItems: 'flex-end' }}>
            <div
              style={{
                width: '100%',
                height: `${(counts[i] / max) * 100}%`,
                background: 'var(--bottle)',
                borderRadius: '3px 3px 0 0',
              }}
            />
          </div>
          <div className="lt-mono lt-tabular" style={{ fontSize: 9, color: 'var(--text-3)' }}>
            {b === 60 ? '1h' : b === 90 ? '1½' : b === 120 ? '2h' : b === 150 ? '2½' : b === 180 ? '3h' : b === 210 ? '3½' : '4h+'}
          </div>
        </div>
      ))}
    </div>
  );
}
